import { FC, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSubscriptionInfo } from '../services/api';
import { getAuthRedirectSearch } from '../pages/marketplace/didarSettingRoute';
import { getMembershipRedirectPath } from '../utils/membershipRedirect';
import Loading from './Loading';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({
  children,
  requireOnboarding = false,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null);

  useEffect(() => {
    const isSubscriptionOnboardingPage = location.pathname === '/onboarding/subscription';
    if (!requireOnboarding || !isSubscriptionOnboardingPage || !user?.user?.is_verified) {
      return;
    }

    let cancelled = false;
    const checkSubscription = async () => {
      setIsCheckingSubscription(true);
      try {
        const response = await getSubscriptionInfo();
        if (!cancelled) {
          setHasActiveSubscription(Boolean(response.has_active_subscription));
        }
      } catch (_error) {
        if (!cancelled) {
          setHasActiveSubscription(false);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingSubscription(false);
        }
      }
    };

    checkSubscription();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, requireOnboarding, user?.user?.is_verified]);

  if (loading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    const authSearch = getAuthRedirectSearch(location.pathname, location.search);
    const redirectPath = `/auth/auth${authSearch}`;
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  const verifiedUserRedirectPath = getMembershipRedirectPath(user?.membership);

  // For onboarding routes
  if (requireOnboarding) {
    const isSubscriptionOnboardingPage = location.pathname === '/onboarding/subscription';
    // Verified users can only open onboarding subscription when there is no active subscription.
    if (user?.user?.is_verified && isSubscriptionOnboardingPage) {
      if (isCheckingSubscription || hasActiveSubscription === null) {
        return <Loading />;
      }
      if (hasActiveSubscription) {
        return <Navigate to={verifiedUserRedirectPath} replace />;
      }
      return <>{children}</>;
    }

    // Other onboarding pages remain blocked for verified users.
    if (user?.user?.is_verified) {
      return <Navigate to={verifiedUserRedirectPath} replace />;
    }
    return <>{children}</>;
  }
  
  // For main routes (dashboard, payments, etc.)
  if (!user?.user?.is_verified) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 